import {useEffect, useId, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {Upload, X} from "lucide-react";
import {useTranslation} from "../../../shared/i18n";
import {validateStaffAvatar} from "../staffAvatarValidation";

const focusableElements = (container) => Array.from(container.querySelectorAll(
    "button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex='-1'])",
));

const blankValues = {
    name: "",
    phone: "",
    phone_number: "",
    login: "",
    position: "",
    branch: "",
    custom_reward_percent: "0",
};

const valuesFrom = (mode, initialValue, branchName) => ({
    ...blankValues,
    name: initialValue?.name || "",
    phone: mode === "create" ? initialValue?.phone || "" : "",
    phone_number: initialValue?.phone_number || "",
    login: initialValue?.login || "",
    position: initialValue?.position || "",
    branch: mode === "create" ? branchName || "" : initialValue?.branch || "",
    custom_reward_percent: String(initialValue?.custom_reward_percent ?? 0),
});

const optionRows = (rows = []) => rows.filter((row) => row?.value && row?.label);

export const StaffEditorDialog = ({
    open,
    mode,
    initialValue,
    branchName,
    properties,
    loading,
    pending,
    avatarPending,
    error,
    onCancel,
    onSubmit,
    onAvatarUpload,
}) => {
    const {t} = useTranslation();
    const titleId = useId();
    const dialogRef = useRef(null);
    const nameRef = useRef(null);
    const invokingElementRef = useRef(null);
    const avatarValidationIdRef = useRef(0);
    const [values, setValues] = useState(blankValues);
    const [avatar, setAvatar] = useState(null);
    const [avatarValidationPending, setAvatarValidationPending] = useState(false);
    const [validationError, setValidationError] = useState("");
    const isCreate = mode === "create";
    const blocked = pending || avatarPending;

    useEffect(() => {
        if (!open) return undefined;
        invokingElementRef.current = document.activeElement;
        setValues(valuesFrom(mode, initialValue, branchName));
        setAvatar(null);
        setAvatarValidationPending(false);
        setValidationError("");
        const frame = window.requestAnimationFrame(() => (
            nameRef.current || focusableElements(dialogRef.current || document.body)[0] || dialogRef.current
        )?.focus());
        return () => {
            avatarValidationIdRef.current += 1;
            window.cancelAnimationFrame(frame);
            const invokingElement = invokingElementRef.current;
            if (invokingElement && document.contains(invokingElement)) invokingElement.focus();
            invokingElementRef.current = null;
        };
    }, [branchName, initialValue, mode, open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                if (!blocked) onCancel();
                return;
            }
            if (event.key !== "Tab" || !dialogRef.current) return;
            const focusable = focusableElements(dialogRef.current);
            if (!focusable.length) {
                event.preventDefault();
                dialogRef.current.focus();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (!dialogRef.current.contains(document.activeElement)) {
                event.preventDefault();
                (event.shiftKey ? last : first).focus();
            } else if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [blocked, onCancel, open]);

    useEffect(() => {
        if (!open || !blocked || !dialogRef.current) return;
        const activeElement = document.activeElement;
        if (!dialogRef.current.contains(activeElement) || activeElement?.disabled) dialogRef.current.focus();
    }, [blocked, open]);

    if (!open) return null;

    const changeValue = (event) => {
        const {name, value} = event.target;
        setValues((current) => ({...current, [name]: value}));
    };

    const submitProfile = (event) => {
        event.preventDefault();
        const required = isCreate
            ? [values.name, values.phone, values.position, values.branch]
            : [values.name, values.login, values.phone_number, values.position];
        if (required.some((value) => !String(value || "").trim())) {
            setValidationError(t("orgs.common.requiredFieldsShort"));
            return;
        }
        const reward = Number(values.custom_reward_percent);
        if (!isCreate && (!Number.isInteger(reward) || reward < 0 || reward > 100)) {
            setValidationError(t("orgs.staffEditor.percent"));
            return;
        }
        setValidationError("");
        if (isCreate) {
            onSubmit({
                name: values.name.trim(),
                phone: values.phone.trim(),
                position: values.position,
                branch: String(branchName || "").trim(),
            });
            return;
        }
        onSubmit({
            name: values.name.trim(),
            login: values.login.trim(),
            phone_number: values.phone_number.trim(),
            position: values.position,
            custom_reward_percent: values.custom_reward_percent,
        });
    };

    const submitAvatar = (event) => {
        event.preventDefault();
        if (!avatar) {
            setValidationError(t("orgs.avatar.choose"));
            return;
        }
        setValidationError("");
        onAvatarUpload(avatar);
    };

    const selectAvatar = async (event) => {
        const file = event.target.files?.[0] || null;
        const validationId = avatarValidationIdRef.current + 1;
        avatarValidationIdRef.current = validationId;
        setAvatar(null);
        setValidationError("");
        if (!file) return;
        setAvatarValidationPending(true);
        try {
            await validateStaffAvatar(file);
            if (avatarValidationIdRef.current === validationId) setAvatar(file);
        } catch (validationFailure) {
            if (avatarValidationIdRef.current === validationId) {
                setValidationError(validationFailure.message || t("orgs.avatar.invalid"));
            }
        } finally {
            if (avatarValidationIdRef.current === validationId) setAvatarValidationPending(false);
        }
    };

    const positions = optionRows(properties?.positions);
    const branches = optionRows(properties?.branches);
    const currentBranchUnavailable = isCreate && !branches.some((option) => option.value === branchName);
    const propertiesMessage = (!positions.length || currentBranchUnavailable) && typeof properties?.message === "string"
        ? properties.message
        : "";
    const message = validationError || error?.message || propertiesMessage;
    const title = isCreate ? t("orgs.staffEditor.createTitle") : t("orgs.staffEditor.editTitle");

    return createPortal(
        <div className="finance-dialog-backdrop" onMouseDown={(event) => {
            if (event.target === event.currentTarget && !blocked) onCancel();
        }}>
            <section
                ref={dialogRef}
                className="finance-dialog staff-editor-dialog"
                role="dialog"
                aria-modal="true"
                aria-busy={blocked || loading}
                aria-labelledby={titleId}
                tabIndex={-1}
            >
                <header className="staff-editor-header">
                    <div>
                        <p>{branchName}</p>
                        <h2 id={titleId}>{title}</h2>
                    </div>
                    <button
                        className="finance-icon-button"
                        type="button"
                        aria-label={t("orgs.common.close")}
                        title={t("orgs.common.close")}
                        disabled={blocked}
                        onClick={onCancel}
                    >
                        <X aria-hidden="true" size={18} />
                    </button>
                </header>

                {loading ? (
                    <p className="staff-editor-loading" role="status">{t("orgs.staffEditor.loading")}</p>
                ) : (
                    <>
                        <form className="staff-editor-form" onSubmit={submitProfile} noValidate>
                            <div className="staff-editor-grid">
                                <label className="staff-field">
                                    <span>{t("orgs.staffEditor.nameLabel")}</span>
                                    <input ref={nameRef} aria-label={t("orgs.staffEditor.name")} name="name" value={values.name} disabled={blocked} onChange={changeValue} />
                                </label>
                                {isCreate ? (
                                    <label className="staff-field">
                                        <span>{t("orgs.staffEditor.phoneLabel")}</span>
                                        <input aria-label={t("orgs.common.phone")} name="phone" value={values.phone} disabled={blocked} onChange={changeValue} />
                                    </label>
                                ) : (
                                    <>
                                        <label className="staff-field">
                                            <span>{t("orgs.staffEditor.phoneLabel")}</span>
                                            <input aria-label={t("orgs.common.phone")} name="phone_number" value={values.phone_number} disabled={blocked} onChange={changeValue} />
                                        </label>
                                        <label className="staff-field">
                                            <span>{t("orgs.staffEditor.loginLabel")}</span>
                                            <input aria-label={t("orgs.common.login")} name="login" autoComplete="username" value={values.login} disabled={blocked} onChange={changeValue} />
                                        </label>
                                    </>
                                )}
                                <label className="staff-field">
                                    <span>{t("orgs.staffEditor.positionLabel")}</span>
                                    <select aria-label={t("orgs.branchForm.position")} name="position" value={values.position} disabled={blocked} onChange={changeValue}>
                                        <option value="">{t("orgs.staffEditor.choosePosition")}</option>
                                        {positions.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                        {values.position && !positions.some((option) => option.value === values.position) ? (
                                            <option value={values.position}>{values.position}</option>
                                        ) : null}
                                    </select>
                                </label>
                                {isCreate ? (
                                    <label className="staff-field">
                                        <span>{t("orgs.staffEditor.branchLabel")}</span>
                                        <input aria-label={t("orgs.common.branch")} name="branch" value={values.branch} disabled={blocked} readOnly />
                                    </label>
                                ) : (
                                    <label className="staff-field">
                                        <span>{t("orgs.staffEditor.percentLabel")}</span>
                                        <input
                                            aria-label={t("orgs.staffEditor.percentLabel")}
                                            name="custom_reward_percent"
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="1"
                                            value={values.custom_reward_percent}
                                            disabled={blocked}
                                            onChange={changeValue}
                                        />
                                    </label>
                                )}
                            </div>
                            {message ? <div className="finance-dialog-error" role="alert"><p>{message}</p></div> : null}
                            <footer className="finance-dialog-footer">
                                <button className="admin-button admin-button-secondary" type="button" disabled={blocked} onClick={onCancel}>{t("orgs.common.cancel")}</button>
                                <button className="admin-button admin-button-primary" type="submit" disabled={blocked}>
                                    {pending
                                        ? (isCreate ? t("orgs.staffEditor.creating") : t("orgs.staffEditor.saving"))
                                        : (isCreate ? t("orgs.staffEditor.create") : t("orgs.common.saveChanges"))}
                                </button>
                            </footer>
                        </form>

                        {!isCreate ? (
                            <form className="staff-avatar-form" onSubmit={submitAvatar}>
                                <div>
                                    <strong>{t("orgs.staffEditor.avatarTitle")}</strong>
                                    <span>{t("orgs.staffEditor.avatarCopy")}</span>
                                </div>
                                <label className="staff-avatar-control">
                                    <Upload aria-hidden="true" size={16} />
                                    <span>{avatarValidationPending ? t("orgs.staffEditor.checking") : avatar?.name || t("orgs.staffEditor.chooseImage")}</span>
                                    <input
                                        aria-label={t("orgs.staffEditor.newAvatar")}
                                        type="file"
                                        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                                        disabled={blocked || avatarValidationPending}
                                        onChange={selectAvatar}
                                    />
                                </label>
                                <button className="admin-button admin-button-secondary" type="submit" disabled={!avatar || blocked || avatarValidationPending}>
                                    {avatarPending ? t("orgs.staffEditor.uploading") : t("orgs.staffEditor.upload")}
                                </button>
                            </form>
                        ) : null}
                    </>
                )}
            </section>
        </div>,
        document.body,
    );
};
